import { Facebook, FileText, Instagram, Mail, ScrollText } from "lucide-react";
import { APP_STORE_URL, EMAIL, FACEBOOK_URL, GOOGLE_PLAY_URL, INSTAGRAM_URL, PRIVACY_URL, TERMS_URL } from "../i18n";

export const Footer = ({ t }) => {
    const links = [
        { label: t.footer.links.instagram, href: INSTAGRAM_URL, icon: Instagram, id: "instagram" },
        { label: t.footer.links.facebook, href: FACEBOOK_URL, icon: Facebook, id: "facebook" },
        { label: t.footer.links.email, href: `mailto:${EMAIL}`, icon: Mail, id: "email" },
        { label: t.footer.links.privacy, href: PRIVACY_URL, icon: FileText, id: "privacy" },
        { label: t.footer.links.terms, href: TERMS_URL, icon: ScrollText, id: "terms" },
    ];

    return (
        <footer className="relative overflow-hidden border-t border-white/10 bg-[#032A44] pb-12 pt-20" data-testid="site-footer">
            <div
                aria-hidden="true"
                className="absolute bottom-[-30%] left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-[#14778D]/15 blur-[130px]"
            />
            <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
                <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
                    <div>
                        <img
                            src="/brand/wordmark-white.webp"
                            alt="TheraOffice"
                            className="h-9 w-auto md:h-11"
                            data-testid="footer-wordmark"
                        />
                        <p className="font-display mt-4 text-lg italic text-[#8FA6AD]" data-testid="footer-tagline">
                            {t.footer.tagline}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-4" data-testid="footer-badges">
                        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="transition-transform duration-300 hover:-translate-y-0.5" data-testid="footer-badge-appstore">
                            <img src="/brand/badge-appstore.png" alt={t.footer.links.appstore} className="h-10 w-auto" />
                        </a>
                        <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="transition-transform duration-300 hover:-translate-y-0.5" data-testid="footer-badge-googleplay">
                            <img src="/brand/badge-googleplay.png" alt={t.footer.links.googleplay} className="h-10 w-auto" />
                        </a>
                    </div>
                </div>

                <nav aria-label="Site sections" className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/5 pt-8" data-testid="footer-nav">
                    {[
                        { href: "/features", label: t.nav.presentation, id: "features" },
                        { href: "/nea", label: t.nav.news, id: "news" },
                        { href: "/#pricing", label: t.nav.pricing, id: "pricing" },
                        { href: "/#download", label: t.nav.cta, id: "download" },
                        { href: "/#about", label: t.nav.about, id: "about" },
                        { href: "/#contact", label: t.nav.contact, id: "contact" },
                    ].map((l) => (
                        <a
                            key={l.id}
                            href={l.href}
                            className="text-sm font-semibold text-[#A3B3B8] transition-colors duration-300 hover:text-[#F7EAB8]"
                            data-testid={`footer-nav-${l.id}`}
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="mt-12 flex flex-col gap-8 border-t border-white/5 pt-10 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-wrap items-center gap-3">
                        {links.map(({ label, href, icon: Icon, id }) => (
                            <a
                                key={id}
                                href={href}
                                target={href.startsWith("http") ? "_blank" : undefined}
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-[#A3B3B8] transition-colors duration-300 hover:border-[#4CC9DC] hover:text-white"
                                data-testid={`footer-link-${id}`}
                            >
                                <Icon size={14} />
                                {label}
                            </a>
                        ))}
                    </div>
                    <div className="text-xs leading-relaxed text-[#8FA6AD]">
                        <p data-testid="footer-made">{t.footer.made}</p>
                        <p className="mt-1" data-testid="footer-rights">{t.footer.rights}</p>
                    </div>
                </div>
            </div>
        </footer>
    );
};
